import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Wallet,
  Search,
  Clock,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  DollarSign,
  Calendar,
  RefreshCw,
  Plus,
  Users,
  XCircle,
  Loader2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Link2
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  getPayments,
  getPendingPayments,
  getOverduePayments,
  verifyPayment,
  rejectPayment,
  getRefunds,
  allocatePayment
} from '../../api/wholesale/payments';
import { useFiscalPeriod } from '../../contexts/FiscalPeriodContext';
import CreatePaymentModal from '../../components/wholesale/CreatePaymentModal';
import PaymentDetailModal from '../../components/wholesale/PaymentDetailModal';
import AllocatePaymentModal from '../../components/wholesale/AllocatePaymentModal';
import PeriodFilter from '../../components/common/PeriodFilter';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const PaymentsPage = () => {
  const { periodRange } = useFiscalPeriod();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchParams] = useSearchParams();
  const statusFilter = searchParams.get('status') || 'all';
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isPaymentDetailModalOpen, setIsPaymentDetailModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [refunds, setRefunds] = useState([]);
  const [loadingRefunds, setLoadingRefunds] = useState(false);
  const [isAllocateModalOpen, setIsAllocateModalOpen] = useState(false);
  const [paymentToAllocate, setPaymentToAllocate] = useState(null);
  const [sortField, setSortField] = useState('payment_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [summary, setSummary] = useState({
    totalOutstanding: 0,
    overdue: 0,
    dueThisWeek: 0,
    collected: 0,
    unallocatedCount: 0,
    unallocatedAmount: 0
  });

  // Local period filter state (null = use global, otherwise override)
  const [localPeriod, setLocalPeriod] = useState(null);

  // Current active date range (local override or global)
  const activeDateRange = useMemo(() => {
    if (localPeriod) {
      return {
        start_date: localPeriod.start_date || null,
        end_date: localPeriod.end_date || null,
        label: localPeriod.label
      };
    }
    return {
      start_date: periodRange?.start_date || null,
      end_date: periodRange?.end_date || null,
      label: periodRange?.period_label || 'All Time'
    };
  }, [localPeriod, periodRange]);

  // Handle period filter change
  const handlePeriodChange = useCallback((range) => {
    if (range.isGlobal) {
      setLocalPeriod(null);
    } else {
      setLocalPeriod(range);
    }
  }, []);

  // Determine if user explicitly chose "All Time" (local override with no dates)
  const isExplicitAllTime = localPeriod && !localPeriod.start_date && !localPeriod.end_date;

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Build date params from active date range
      const dateParams = {};
      if (activeDateRange.start_date) dateParams.start_date = activeDateRange.start_date;
      if (activeDateRange.end_date) dateParams.end_date = activeDateRange.end_date;

      // Tell backend to skip global fiscal period fallback when "All Time" is explicitly selected
      if (isExplicitAllTime && !dateParams.start_date && !dateParams.end_date) {
        dateParams.ignore_global_period = true;
      }

      const response = await getPayments({ ...dateParams, limit: 1000 });

      const data = response?.data || response || [];
      const paymentsList = Array.isArray(data) ? data : [];
      setPayments(paymentsList);

      // Calculate summary from data
      const now = new Date();
      const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

      let outstanding = 0;
      let overdueAmount = 0;
      let dueThisWeekAmount = 0;
      let collectedAmount = 0;
      let unallocatedCount = 0;
      let unallocatedAmount = 0;

      paymentsList.forEach((p) => {
        // Backend returns payment_amount for wholesale_payments table
        const paymentAmount = parseFloat(p.payment_amount) || parseFloat(p.amount) || 0;
        const status = p.payment_status || p.status || 'pending';
        const allocationStatus = p.allocation_status || 'unallocated';

        // Track unallocated payments separately
        if (allocationStatus === 'unallocated' && status !== 'cancelled' && status !== 'rejected') {
          unallocatedCount++;
          unallocatedAmount += paymentAmount;
        }

        if (status === 'verified' || status === 'paid') {
          // Only count allocated payments in the collected total
          if (allocationStatus === 'allocated') {
            collectedAmount += paymentAmount;
          }
        } else if (status !== 'cancelled' && status !== 'rejected') {
          // Pending payments are considered outstanding (only if allocated)
          if (allocationStatus === 'allocated') {
            outstanding += paymentAmount;
          }
        }
      });

      setSummary({
        totalOutstanding: outstanding,
        overdue: overdueAmount,
        dueThisWeek: dueThisWeekAmount,
        collected: collectedAmount,
        unallocatedCount,
        unallocatedAmount
      });
    } catch (err) {
      console.error('Error fetching payments:', err);
      setError('Failed to load payments');
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [activeDateRange.start_date, activeDateRange.end_date]);

  const fetchRefunds = useCallback(async () => {
    setLoadingRefunds(true);
    try {
      const dateParams = {};
      if (activeDateRange.start_date) dateParams.date_from = activeDateRange.start_date;
      if (activeDateRange.end_date) dateParams.date_to = activeDateRange.end_date;
      if (searchQuery) dateParams.search = searchQuery;

      // Tell backend to skip global fiscal period fallback when "All Time" is explicitly selected
      if (isExplicitAllTime && !dateParams.date_from && !dateParams.date_to) {
        dateParams.ignore_global_period = true;
      }

      const response = await getRefunds({ ...dateParams, limit: 50 });

      setRefunds(response?.data || []);
    } catch (err) {
      console.error('Error fetching refunds:', err);
      toast.error('Failed to load refunds');
      setRefunds([]);
    } finally {
      setLoadingRefunds(false);
    }
  }, [activeDateRange.start_date, activeDateRange.end_date, searchQuery]);

  useEffect(() => {
    if (activeTab === 'refunds') {
      fetchRefunds();
    } else {
      fetchPayments();
    }
  }, [activeTab, fetchPayments, fetchRefunds]);

  const statusConfig = {
    pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
    partial: { label: 'Partial', color: 'bg-blue-100 text-blue-700', icon: DollarSign },
    verified: { label: 'Verified', color: 'bg-green-100 text-green-700', icon: CheckCircle },
    paid: { label: 'Paid', color: 'bg-green-100 text-green-700', icon: CheckCircle },
    rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: AlertCircle },
    overdue: { label: 'Overdue', color: 'bg-red-100 text-red-700', icon: AlertCircle }
  };

  const tabs = [
    { id: 'all', label: 'All Payments' },
    { id: 'pending', label: 'Pending' },
    { id: 'overdue', label: 'Overdue' },
    { id: 'verified', label: 'Verified' },
    {
      id: 'unallocated',
      label: `Unallocated${summary.unallocatedCount > 0 ? ` (${summary.unallocatedCount})` : ''}`
    }
  ];

  const filteredPayments = payments.filter((payment) => {
    const orderNumber = payment.order_number || '';
    const paymentNumber = payment.payment_number || '';
    // Backend returns distributor_business_name
    const distributorName =
      payment.distributor_business_name || payment.business_name || payment.distributor_name || '';
    const status = payment.payment_status || payment.status || '';

    const matchesSearch =
      orderNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      paymentNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      distributorName.toLowerCase().includes(searchQuery.toLowerCase());

    // Tab filtering
    if (activeTab === 'unallocated') {
      const allocationStatus = payment.allocation_status || 'unallocated';
      const pStatus = payment.payment_status || payment.status || '';
      return (
        matchesSearch &&
        allocationStatus === 'unallocated' &&
        pStatus !== 'cancelled' &&
        pStatus !== 'rejected'
      );
    }

    if (statusFilter === 'all' && activeTab === 'all') return matchesSearch;
    const filterValue = activeTab !== 'all' ? activeTab : statusFilter;
    if (filterValue === 'all') return matchesSearch;
    return matchesSearch && status === filterValue;
  });

  // Sorting logic
  const sortedPayments = [...filteredPayments].sort((a, b) => {
    let aValue, bValue;

    switch (sortField) {
      case 'payment_date':
        aValue = new Date(a.payment_date || 0);
        bValue = new Date(b.payment_date || 0);
        break;
      case 'amount':
        aValue = parseFloat(a.payment_amount) || parseFloat(a.amount) || 0;
        bValue = parseFloat(b.payment_amount) || parseFloat(b.amount) || 0;
        break;
      case 'status':
        aValue = a.payment_status || a.status || '';
        bValue = b.payment_status || b.status || '';
        break;
      case 'method':
        aValue = a.payment_method || '';
        bValue = b.payment_method || '';
        break;
      case 'type':
        aValue = a.allocation_type || '';
        bValue = b.allocation_type || '';
        break;
      case 'distributor':
        aValue = a.distributor_business_name || a.business_name || a.distributor_name || '';
        bValue = b.distributor_business_name || b.business_name || b.distributor_name || '';
        break;
      case 'payment_number':
        aValue = a.payment_number || '';
        bValue = b.payment_number || '';
        break;
      default:
        return 0;
    }

    // Handle different data types
    if (aValue instanceof Date && bValue instanceof Date) {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }

    // String comparison
    const comparison = String(aValue).localeCompare(String(bValue));
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  // Pagination
  const totalItems = sortedPayments.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedPayments = sortedPayments.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, activeDateRange]);

  const formatAmount = (amount) => {
    if (amount >= 1000000) {
      return `₦${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `₦${(amount / 1000).toFixed(0)}K`;
    }
    return `₦${amount.toLocaleString()}`;
  };

  const getAllocationColor = (type, allocationStatus) => {
    if (allocationStatus === 'unallocated') return 'bg-red-100 text-red-800';
    switch (type) {
      case 'specific_order':
      case 'order_payment':
        return 'bg-blue-100 text-blue-800';
      case 'account_balance':
        return 'bg-purple-100 text-purple-800';
      case 'outstanding_payment':
        return 'bg-teal-100 text-teal-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getAllocationLabel = (type, allocationStatus) => {
    if (allocationStatus === 'unallocated') return '⚠ Unallocated';
    switch (type) {
      case 'specific_order':
      case 'order_payment':
        return 'Order Payment';
      case 'account_balance':
        return 'Account Balance';
      case 'outstanding_payment':
        return 'Credit Payment';
      default:
        return type ? type.replace(/_/g, ' ') : 'N/A';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified':
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
    return sortDirection === 'asc' ? (
      <ArrowUp className="w-4 h-4 text-emerald-600" />
    ) : (
      <ArrowDown className="w-4 h-4 text-emerald-600" />
    );
  };

  if (loading && payments.length === 0 && activeTab !== 'refunds') {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payments</h1>
          <p className="text-gray-500 mt-1">
            Track and manage order payments
            {localPeriod && (
              <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                {activeDateRange.label}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <PeriodFilter
            onChange={handlePeriodChange}
            showLabel={true}
            compact={false}
            storageKey="wholesale_payments"
          />
          <button
            onClick={activeTab === 'refunds' ? fetchRefunds : fetchPayments}
            className="inline-flex items-center gap-2 px-3 py-2 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading || loadingRefunds ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            <Plus className="w-5 h-5" />
            Record Payment
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-gray-500 text-sm">Total Pending</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {formatAmount(summary.totalOutstanding)}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-gray-500 text-sm">Overdue</p>
          <p className="text-2xl font-bold text-red-600 mt-1">{formatAmount(summary.overdue)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-gray-500 text-sm">Due This Week</p>
          <p className="text-2xl font-bold text-amber-600 mt-1">
            {formatAmount(summary.dueThisWeek)}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-gray-500 text-sm">Collected (Allocated)</p>
          <p className="text-2xl font-bold text-emerald-600 mt-1">
            {formatAmount(summary.collected)}
          </p>
        </div>
        {summary.unallocatedCount > 0 && (
          <div className="bg-red-50 rounded-xl border-2 border-red-300 p-4">
            <p className="text-red-600 text-sm font-medium flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" />
              Unallocated ({summary.unallocatedCount})
            </p>
            <p className="text-2xl font-bold text-red-600 mt-1">
              {formatAmount(summary.unallocatedAmount)}
            </p>
            <p className="text-xs text-red-500 mt-1">Not in financial reports</p>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                navigate(
                  tab.id === 'all' ? '/wholesale/payments' : `/wholesale/payments?status=${tab.id}`
                );
              }}
              className={`py-3 border-b-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-emerald-500 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
          <button
            onClick={() => setActiveTab('refunds')}
            className={`py-3 border-b-2 text-sm font-medium transition-colors ${
              activeTab === 'refunds'
                ? 'border-emerald-500 text-emerald-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Refunds
          </button>
        </nav>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search payments..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
        />
      </div>

      {/* Empty State */}
      {!loading && !loadingRefunds && filteredPayments.length === 0 && activeTab !== 'refunds' && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Wallet className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No payments found</h3>
          <p className="text-gray-500 mb-4">
            {searchQuery || statusFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'Payment records will appear here'}
          </p>
        </div>
      )}

      {/* Payments Table */}
      {activeTab === 'refunds' ? (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Distributor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Method
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Processed By
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loadingRefunds ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                      Loading refunds...
                    </td>
                  </tr>
                ) : refunds.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                      No refunds found
                    </td>
                  </tr>
                ) : (
                  refunds.map((refund) => (
                    <tr key={refund.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(refund.transaction_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {refund.distributor_name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-bold text-red-600">
                          -₦{parseFloat(refund.amount).toLocaleString()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                        {refund.refund_method}
                      </td>
                      <td
                        className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate"
                        title={refund.description}
                      >
                        {refund.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {refund.created_by}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        sortedPayments.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-6 py-4">
                      <button
                        onClick={() => handleSort('payment_date')}
                        className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-emerald-600 transition-colors"
                      >
                        Date & Time
                        <SortIcon field="payment_date" />
                      </button>
                    </th>
                    <th className="text-left px-6 py-4">
                      <button
                        onClick={() => handleSort('payment_number')}
                        className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-emerald-600 transition-colors"
                      >
                        Payment #
                        <SortIcon field="payment_number" />
                      </button>
                    </th>
                    <th className="text-left px-6 py-4">
                      <button
                        onClick={() => handleSort('distributor')}
                        className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-emerald-600 transition-colors"
                      >
                        Distributor
                        <SortIcon field="distributor" />
                      </button>
                    </th>
                    <th className="text-right px-6 py-4">
                      <button
                        onClick={() => handleSort('amount')}
                        className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-emerald-600 transition-colors ml-auto"
                      >
                        Amount
                        <SortIcon field="amount" />
                      </button>
                    </th>
                    <th className="text-left px-6 py-4">
                      <button
                        onClick={() => handleSort('method')}
                        className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-emerald-600 transition-colors"
                      >
                        Method
                        <SortIcon field="method" />
                      </button>
                    </th>
                    <th className="text-left px-6 py-4">
                      <button
                        onClick={() => handleSort('type')}
                        className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-emerald-600 transition-colors"
                      >
                        Type
                        <SortIcon field="type" />
                      </button>
                    </th>
                    <th className="text-left px-6 py-4">
                      <button
                        onClick={() => handleSort('status')}
                        className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-emerald-600 transition-colors"
                      >
                        Status
                        <SortIcon field="status" />
                      </button>
                    </th>
                    <th className="text-right px-6 py-4 text-sm font-semibold text-gray-900">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedPayments.map((payment) => {
                    const paymentStatus = payment.payment_status || payment.status || 'pending';
                    const status = statusConfig[paymentStatus] || statusConfig.pending;
                    const StatusIcon = status?.icon || Clock;
                    // Backend returns payment_amount for the payment record
                    const paymentAmount =
                      parseFloat(payment.payment_amount) || parseFloat(payment.amount) || 0;
                    // For wholesale_payments, we show the payment amount directly
                    const distributorName =
                      payment.distributor_business_name ||
                      payment.business_name ||
                      payment.distributor_name ||
                      '-';
                    const isUnallocated =
                      (payment.allocation_status || 'unallocated') === 'unallocated' &&
                      paymentStatus !== 'cancelled' &&
                      paymentStatus !== 'rejected';

                    return (
                      <tr
                        key={payment.id}
                        className={`transition-colors cursor-pointer ${
                          isUnallocated
                            ? 'bg-red-50 hover:bg-red-100 border-l-4 border-l-red-500'
                            : 'hover:bg-gray-50'
                        }`}
                        onClick={() => {
                          setSelectedPayment(payment);
                          setIsPaymentDetailModalOpen(true);
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {payment.payment_date
                            ? new Date(payment.payment_date).toLocaleString()
                            : '-'}
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-medium text-blue-600">
                            {payment.payment_number || '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-900">
                          <div className="text-sm font-medium text-gray-900">{distributorName}</div>
                          <div className="text-sm text-gray-500">
                            {payment.distributor_number || ''}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right font-medium text-gray-900">
                          ₦{paymentAmount.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-gray-500 capitalize">
                          {payment.payment_method ? payment.payment_method.replace('_', ' ') : '-'}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getAllocationColor(
                              payment.allocation_type,
                              payment.allocation_status
                            )}`}
                          >
                            {getAllocationLabel(payment.allocation_type, payment.allocation_status)}
                          </span>
                          {isUnallocated && (
                            <div className="text-xs text-red-500 mt-1 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              Not in reports
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${status?.color}`}
                          >
                            <StatusIcon className="w-3 h-3" />
                            {status?.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            {isUnallocated && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPaymentToAllocate(payment);
                                  setIsAllocateModalOpen(true);
                                }}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200 transition-colors"
                              >
                                <Link2 className="w-3 h-3" />
                                Allocate
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedPayment(payment);
                                setIsPaymentDetailModalOpen(true);
                              }}
                              className="text-emerald-600 hover:text-emerald-900"
                            >
                              View
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
                <div className="text-sm text-gray-500">
                  Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems}{' '}
                  payments
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      )}

      {/* Create Payment Modal */}
      <CreatePaymentModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          fetchPayments();
          setIsCreateModalOpen(false);
        }}
      />

      {/* Payment Detail Modal */}
      <PaymentDetailModal
        payment={selectedPayment}
        isOpen={isPaymentDetailModalOpen}
        onClose={() => {
          setIsPaymentDetailModalOpen(false);
          setSelectedPayment(null);
        }}
        onSuccess={() => {
          fetchPayments();
          setIsPaymentDetailModalOpen(false);
          setSelectedPayment(null);
        }}
        onAllocate={(payment) => {
          setIsPaymentDetailModalOpen(false);
          setSelectedPayment(null);
          setPaymentToAllocate(payment);
          setIsAllocateModalOpen(true);
        }}
      />

      {/* Allocate Payment Modal */}
      <AllocatePaymentModal
        payment={paymentToAllocate}
        isOpen={isAllocateModalOpen}
        onClose={() => {
          setIsAllocateModalOpen(false);
          setPaymentToAllocate(null);
        }}
        onSuccess={() => {
          fetchPayments();
          setIsAllocateModalOpen(false);
          setPaymentToAllocate(null);
        }}
      />
    </div>
  );
};

export default PaymentsPage;
